import { createContext, useMemo, type ReactNode } from 'react'
import { useAsync } from 'react-use'
import {
  CRM_INTEGRATION_CHAT_AVAILABILITY_EMPTY,
  fetchCrmIntegrationChatAvailability,
  hasAnyCrmIntegrationChatConnected,
  type CrmIntegrationChatAvailabilityMap,
} from '@pages/Admin/CRM/utils/crmIntegrationChatAvailability'
import type { IntegrationItem } from '@/api/integrations/integrations.types'
import {
  VK_INTEGRATION_LEAD_CHAT_UNAVAILABLE,
  type VkIntegrationLeadChatAvailability,
} from '@pages/Admin/CRM/utils/vkIntegrationLeadChat'

export type CrmIntegrationChatAvailabilityState = {
  isLoading: boolean
  providers: CrmIntegrationChatAvailabilityMap
  hasAnyConnected: boolean
  vkLeadChat: VkIntegrationLeadChatAvailability
  connectedIntegrations: IntegrationItem[]
}

const LOADING_STATE: CrmIntegrationChatAvailabilityState = {
  isLoading: true,
  providers: CRM_INTEGRATION_CHAT_AVAILABILITY_EMPTY,
  hasAnyConnected: false,
  vkLeadChat: VK_INTEGRATION_LEAD_CHAT_UNAVAILABLE,
  connectedIntegrations: [],
}

export const CrmIntegrationChatAvailabilityContext =
  createContext<CrmIntegrationChatAvailabilityState | undefined>(undefined)

export const CrmIntegrationChatAvailabilityProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const state = useAsync(fetchCrmIntegrationChatAvailability, [])

  const value = useMemo((): CrmIntegrationChatAvailabilityState => {
    if (state.loading) {
      return LOADING_STATE
    }

    const result = state.value
    const providers =
      result?.providers ?? CRM_INTEGRATION_CHAT_AVAILABILITY_EMPTY

    return {
      isLoading: false,
      providers,
      hasAnyConnected: hasAnyCrmIntegrationChatConnected(providers),
      vkLeadChat: result?.vkLeadChat ?? VK_INTEGRATION_LEAD_CHAT_UNAVAILABLE,
      connectedIntegrations: result?.connectedIntegrations ?? [],
    }
  }, [state.loading, state.value])

  return (
    <CrmIntegrationChatAvailabilityContext.Provider value={value}>
      {children}
    </CrmIntegrationChatAvailabilityContext.Provider>
  )
}
